import { useInView } from 'react-intersection-observer'
import { css } from '@emotion/react'
import { useEffect } from 'react'
import { mq } from 'plume-ui'

const STICKY_HEADER_OFFSET = '-220px 0px 0px 0px'

type Props<TabType> = {
  tabName: TabType
  offset: string
  mobileOffset?: string
  className?: string
  stickyOffset?: string
  children: React.ReactNode
  onChangeActive: (tab: TabType, inView: boolean) => void
}

export const InViewForActiveTab = <TabType,>(props: Props<TabType>) => {
  const mobileOffset = props.mobileOffset || props.offset
  const [ref, inView] = useInView({
    threshold: [0, 1],
    rootMargin: props.stickyOffset || STICKY_HEADER_OFFSET,
  })

  useEffect(() => {
    props.onChangeActive(props.tabName, inView)
  }, [inView])

  return (
    <div>
      <AnchorWithOffset<TabType>
        id={props.tabName}
        offset={props.offset}
        mobileOffset={mobileOffset}
      />
      <div
        ref={ref}
        id={props.tabName + '-contents'}
        className={props.className}
      >
        {props.children}
      </div>
    </div>
  )
}

// Tabを押したときにここにスクロールするための空要素。
type AnchorWithOffsetProps<TabType> = {
  id: TabType
  offset: string
  mobileOffset: string
}
const AnchorWithOffset = <TabType,>({
  id,
  offset,
  mobileOffset,
}: AnchorWithOffsetProps<TabType>) => (
  <div
    id={`${id}`}
    css={css`
      display: block;
      position: relative;
      top: ${offset};
      visibility: hidden;
      ${mq.untilMedium} {
        top: ${mobileOffset};
      }
    `}
  />
)

// 同時に発生するIntersectionObserver(useInView)のイベントを一度キューに溜めてから、
// まとめてタブのアクティブ状態を変更するためにわざとグローバル変数にしています。
// イベントをReactのライフサイクル内で処理しようとするとrace conditionになり動作が安定しません。
type ChangeEvent<TabType> = [TabType, boolean]
type PushQueueFn<TabType> = (
  tab: TabType,
  inView: boolean,
  fn: () => void
) => void
type ApplyFromQueueFn<TabType extends string> = (
  map: ActiveTabMap<TabType>
) => ActiveTabMap<TabType>
export const createInViewEventQueue = <TabType extends string>(
  duration: number = 50
) => {
  let queue: ChangeEvent<TabType>[] = []
  let handle: any = null

  const push = (tab: TabType, inView: boolean, timeoutFn: () => void) => {
    queue.push([tab, inView])
    if (handle) {
      clearTimeout(handle)
    }
    handle = setTimeout(timeoutFn, duration)
  }

  const apply = (activeTabs: ActiveTabMap<TabType>): ActiveTabMap<TabType> => {
    const newTabs = { ...activeTabs }
    for (const [tab, state] of queue) {
      newTabs[tab] = state
    }
    queue = [] // clear
    return newTabs
  }

  return [push, apply] as [PushQueueFn<TabType>, ApplyFromQueueFn<TabType>]
}

export type ActiveTabMap<TabType extends string> = { [key in TabType]: boolean }
type InitFn<TabType extends string> = () => ActiveTabMap<TabType>
type GetFirstFn<TabType extends string> = (
  map: ActiveTabMap<TabType>
) => TabType | null
export const setupActiveTabs = <TabType extends string>(list: TabType[]) => {
  const init = () =>
    Object.fromEntries(list.map((tab) => [tab, false])) as ActiveTabMap<TabType>

  const first = (activeTabs: ActiveTabMap<TabType>) => {
    for (const tab of list) {
      if (activeTabs[tab]) {
        return tab as TabType
      }
    }
    return null
  }

  return [init, first] as [InitFn<TabType>, GetFirstFn<TabType>]
}
